import React from 'react';
import Modal from 'react-modal';
import { COST_CODE_REF, CREW_REF } from './TimesheetsPage';

/**
 * Utility function to determine used cost codes for the dynamic table.
 */
function getUsedCostCodes(timesheet = []) {
  const usedCodes = new Set();
  timesheet.forEach((worker) => {
    if (!worker.absent && worker.costCodes) {
      Object.keys(worker.costCodes).forEach((code) => {
        const c = worker.costCodes[code];
        const total = (c.hoursStandard || 0) + (c.hoursOT || 0) + (c.hoursDouble || 0);
        if (total > 0) usedCodes.add(code);
      });
    }
  });
  return Array.from(usedCodes);
}

/**
 * Helper to get cost code descriptions.
 */
function getCodeDescription(code) {
  const found = COST_CODE_REF.find((item) => item.code === code);
  return found ? found.desc : '';
}

function TimesheetsModal({
  modalOpen,
  closeModal,
  recording,
  startRecording,
  stopRecording,
  transcript,
  setTranscript,
  isAnalyzing,
  analyzeTranscript,
  handleSaveAndDownloadPdf,
  parsedActivities,
  setParsedActivities,
}) {
  // Handler for updating ST/OT/DT hours
  const handleCostCodeChange = (workerIndex, code, field, value) => {
    const updated = { ...parsedActivities };
    const w = updated.timesheet[workerIndex];
    const newValue = parseFloat(value) || 0;
    if (newValue < 0) return; // Prevent negative values
    w.costCodes[code][field] = newValue;

    let newTotal = 0;
    Object.keys(w.costCodes).forEach((cc) => {
      newTotal += (w.costCodes[cc].hoursStandard || 0) + (w.costCodes[cc].hoursOT || 0) + (w.costCodes[cc].hoursDouble || 0);
    });
    w.totalHours = newTotal;
    w.mealTicket = newTotal >= 10;
    if (newTotal > 0) w.absent = false;
    setParsedActivities(updated);
  };

  // Handler for toggling absent status
  const handleAbsentChange = (workerIndex, isAbsent) => {
    const updated = { ...parsedActivities };
    const w = updated.timesheet[workerIndex];
    w.absent = isAbsent;
    if (isAbsent) {
      Object.keys(w.costCodes).forEach((code) => {
        w.costCodes[code].hoursStandard = 0;
        w.costCodes[code].hoursOT = 0;
        w.costCodes[code].hoursDouble = 0;
      });
      w.totalHours = 0;
      w.mealTicket = false;
    }
    setParsedActivities(updated);
  };

  // Render table headers for the timesheet
  const renderTableHeaders = (usedCodes) => (
    <thead className="bg-gray-700 text-white">
      <tr>
        <th className="p-2 border border-gray-600 text-left">Name</th>
        <th className="p-2 border border-gray-600 text-left">Nickname</th>
        {usedCodes.map((code) => (
          <th key={code} colSpan={3} className="p-2 border border-gray-600 text-center">
            {code} - {getCodeDescription(code)}
          </th>
        ))}
        <th className="p-2 border border-gray-600 text-left">Total</th>
        <th className="p-2 border border-gray-600 text-left">Meal Ticket</th>
        <th className="p-2 border border-gray-600 text-left">Absent</th>
      </tr>
      <tr className="bg-gray-600">
        <th className="p-2 border border-gray-600"></th>
        <th className="p-2 border border-gray-600"></th>
        {usedCodes.map((code) => (
          <React.Fragment key={code}>
            <th className="p-2 border border-gray-600">ST</th>
            <th className="p-2 border border-gray-600">OT</th>
            <th className="p-2 border border-gray-600">DT</th>
          </React.Fragment>
        ))}
        <th className="p-2 border border-gray-600"></th>
        <th className="p-2 border border-gray-600"></th>
        <th className="p-2 border border-gray-600"></th>
      </tr>
    </thead>
  );

  // Render table body for the timesheet
  const renderTableBody = (usedCodes) => {
    if (!parsedActivities.timesheet) return null;
    return parsedActivities.timesheet.map((worker, wIdx) => (
      <tr key={wIdx} className="even:bg-gray-800">
        <td className="p-2 border border-gray-600">{worker.name}</td>
        <td className="p-2 border border-gray-600">{worker.nickname}</td>
        {usedCodes.map((code) => {
          const stVal = worker.costCodes?.[code]?.hoursStandard || 0;
          const otVal = worker.costCodes?.[code]?.hoursOT || 0;
          const dtVal = worker.costCodes?.[code]?.hoursDouble || 0;
          return (
            <React.Fragment key={`${code}-${wIdx}`}>
              <td className="p-2 border border-gray-600">
                <input
                  className="w-full p-1 bg-gray-700 text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  type="number"
                  value={stVal}
                  disabled={worker.absent}
                  onChange={(e) => handleCostCodeChange(wIdx, code, 'hoursStandard', e.target.value)}
                  aria-label={`Standard hours for ${code}`}
                />
              </td>
              <td className="p-2 border border-gray-600">
                <input
                  className="w-full p-1 bg-gray-700 text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  type="number"
                  value={otVal}
                  disabled={worker.absent}
                  onChange={(e) => handleCostCodeChange(wIdx, code, 'hoursOT', e.target.value)}
                  aria-label={`Overtime hours for ${code}`}
                />
              </td>
              <td className="p-2 border border-gray-600">
                <input
                  className="w-full p-1 bg-gray-700 text-white rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  type="number"
                  value={dtVal}
                  disabled={worker.absent}
                  onChange={(e) => handleCostCodeChange(wIdx, code, 'hoursDouble', e.target.value)}
                  aria-label={`Double time hours for ${code}`}
                />
              </td>
            </React.Fragment>
          );
        })}
        <td className="p-2 border border-gray-600">{worker.totalHours}</td>
        <td className="p-2 border border-gray-600 text-center">
          <input type="checkbox" checked={worker.mealTicket || false} readOnly aria-label="Meal Ticket" />
        </td>
        <td className="p-2 border border-gray-600 text-center">
          <input
            type="checkbox"
            checked={worker.absent || false}
            onChange={(e) => handleAbsentChange(wIdx, e.target.checked)}
            aria-label="Absent"
          />
        </td>
      </tr>
    ));
  };

  const usedCodes = getUsedCostCodes(parsedActivities.timesheet);

  return (
    <Modal
      isOpen={modalOpen}
      onRequestClose={closeModal}
      contentLabel="Timesheet Flow"
      className="bg-gray-800 text-white max-w-4xl mx-auto my-4 p-6 rounded-lg shadow-lg sm:max-w-full sm:my-2 sm:p-4 overflow-y-auto max-h-[80vh]"
      overlayClassName="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center"
    >
      {/* Centered Logo */}
      <div className="flex justify-center mb-6">
        <img src="/LogoFile.png" alt="Myte Logo" className="w-32 sm:w-24" />
      </div>

      {/* Title */}
      <h2 className="text-2xl font-bold text-center mb-6">New Timesheet</h2>

      {/* Cost Codes Reference */}
      <h4 className="text-xl font-semibold mb-2">Cost Codes Reference</h4>
      <div className="overflow-x-auto mb-6">
        <table className="w-full border-collapse">
          <thead className="bg-gray-700 text-white">
            <tr>
              <th className="p-2 border border-gray-600 text-left">Code</th>
              <th className="p-2 border border-gray-600 text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            {COST_CODE_REF.map(({ code, desc }) => (
              <tr key={code} className="even:bg-gray-700">
                <td className="p-2 border border-gray-600">{code}</td>
                <td className="p-2 border border-gray-600">{desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Crew Members Reference */}
      <h4 className="text-xl font-semibold mb-2">Crew Members Reference</h4>
      <div className="overflow-x-auto mb-6">
        <table className="w-full border-collapse">
          <thead className="bg-gray-700 text-white">
            <tr>
              <th className="p-2 border border-gray-600 text-left">Name</th>
              <th className="p-2 border border-gray-600 text-left">Nickname</th>
            </tr>
          </thead>
          <tbody>
            {CREW_REF.map((c) => (
              <tr key={c.nickname} className="even:bg-gray-700">
                <td className="p-2 border border-gray-600">{c.name}</td>
                <td className="p-2 border border-gray-600">{c.nickname}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Recording Controls */}
      <div className="flex justify-center mb-6">
        <button
          onClick={recording ? stopRecording : startRecording}
          className={`px-4 py-2 rounded-md text-white font-medium transition-colors ${
            recording ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          {recording ? 'Stop Recording' : 'Start Recording'}
        </button>
      </div>

      {/* Transcript Input */}
      <div className="relative mb-6">
        <label className="block mb-2 text-sm font-medium">Transcript:</label>
        <textarea
          className="w-full h-32 p-2 bg-gray-700 text-white rounded-md border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
          value={transcript}
          onChange={(e) => setTranscript(e.target.value)}
          placeholder="Enter or view transcript here..."
        />
        {isAnalyzing && (
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 text-white text-lg animate-pulse rounded-md">
            I'm thinking...
          </div>
        )}
      </div>

      {/* Modal Controls */}
      <div className="flex flex-wrap justify-center gap-4 mb-6 sm:flex-col sm:gap-2">
        <button
          onClick={analyzeTranscript}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          Analyze with Myte
        </button>
        <button
          onClick={handleSaveAndDownloadPdf}
          className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
        >
          Save & Download PDF
        </button>
      </div>

      {/* Timesheet Review */}
      <h4 className="text-xl font-semibold mb-2">Timesheet</h4>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          {renderTableHeaders(usedCodes)}
          <tbody>{renderTableBody(usedCodes)}</tbody>
        </table>
      </div>

      {/* Close Button */}
      <div className="mt-6 flex justify-center">
        <button
          onClick={closeModal}
          className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
        >
          Close
        </button>
      </div>
    </Modal>
  );
}

export default TimesheetsModal;